import { Notice } from 'ui';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';

import classes from '../Chat.module.scss';
import type { ContactShareState } from '../types';
import { canSubmitReview } from '../utils/dealProgress';
import {
  formatReviewWindowCountdown,
  resolveReviewWindowEndsAtMs,
} from '../utils/reviewWindowCountdown';

interface ReviewWindowPanelProps {
  contactShare: ContactShareState | null;
  onLeaveReview: () => void;
}

export const ReviewWindowPanel: React.FC<ReviewWindowPanelProps> = ({
  contactShare,
  onLeaveReview,
}) => {
  const endsAtMs = useMemo(
    () =>
      resolveReviewWindowEndsAtMs(
        contactShare?.review_window_ends_at,
        contactShare?.seconds_until_review_window,
      ),
    [contactShare?.review_window_ends_at, contactShare?.seconds_until_review_window],
  );

  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (endsAtMs == null || contactShare?.has_left_review) {
      return undefined;
    }
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [endsAtMs, contactShare?.has_left_review]);

  if (!contactShare?.shared) {
    return null;
  }

  const alreadyReviewed = Boolean(contactShare.has_left_review);
  const canLeave = canSubmitReview(contactShare);
  const countdown = formatReviewWindowCountdown(endsAtMs, nowMs);
  const hasTimer = endsAtMs != null && !alreadyReviewed;
  const expired = hasTimer ? countdown.expired : false;

  return (
    <Notice
      collapsible
      collapsedLabel='Отзыв о занятии'
      tone={alreadyReviewed ? 'success' : expired ? 'warning' : 'info'}
      action={
        <button
          type='button'
          className={classes['review-window-bar__cta']}
          onClick={onLeaveReview}
          disabled={!canLeave}
          title={alreadyReviewed ? 'Вы уже оставили отзыв по этому репетитору' : undefined}
        >
          {alreadyReviewed ? 'Отзыв уже оставлен' : 'Оставить отзыв'}
        </button>
      }
    >
      <div className={classes['review-window-bar__text']}>
        {alreadyReviewed ? (
          <p>Спасибо! Вы уже оставили отзыв об этом репетиторе.</p>
        ) : hasTimer && !expired ? (
          <>
            <p>
              Контакты переданы. Оставьте отзыв о репетиторе - окно закрывается через{' '}
              <strong className={classes['review-window-bar__timer']}>{countdown.label}</strong>.
            </p>
            <p className={classes['review-window-bar__hint']}>
              Можно оставить отзыв прямо сейчас, не дожидаясь окончания таймера.
            </p>
          </>
        ) : hasTimer && expired ? (
          <p>
            Время на отзыв истекло. Сделка закрыта автоматически - вы все еще можете оставить
            отзыв.
          </p>
        ) : (
          <p>Контакты переданы. Оцените работу репетитора, пока свежи впечатления.</p>
        )}
      </div>
    </Notice>
  );
};
